import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Avatar from "./Avatar";
import CollaboratorBadgesExport from "./CollaboratorBadgesExport";
import Footer from "./Footer";
import {
  getCategoryColor,
  getThemeColor,
  getSeliseCSuiteColor,
  hexToRgba,
  getBadgeColor,
  getBoardColor,
  getBgColor,
  getDividerColor,
  getFooterColor,
} from "./seliseColors";

const ROLE_ORDER = ["VP", "Director", "Head", "Manager"];
const ROLE_LABELS = { VP: "Vice President" };
const CATEGORIES = [
  { category: "industry", label: "Industry Units" },
  { category: "product", label: "Product Units" },
  { category: "service", label: "Service Units" },
  { category: "support", label: "Support Units" },
];

function getCLevelColor(cSuiteNodes, cLevel, theme) {
  const index = cSuiteNodes.findIndex((c) => c.id === cLevel.id);
  const fallback = theme === "selise" || theme === "dark" ? getSeliseCSuiteColor(index) : "#3465a4";
  return getThemeColor(cLevel.color, theme, fallback);
}

function PersonRowExport({ person, cSuiteNodes, theme, allPeople }) {
  const assigned = cSuiteNodes?.find((c) => c.id === person.c_level_id);
  return (
    <div className="flex items-center gap-2 py-0.5 px-1">
      <Avatar name={person.name} photoUrl={person.photo_url} size="sm" />
      <div className="min-w-0 flex-1 flex items-center gap-1.5">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 leading-tight truncate">
            {person.is_vacancy ? "Open Position" : person.name}
          </p>
          <p className="text-xs text-gray-500 leading-tight truncate">
            {person.function_area || ""}
            {person.seniority ? ` (${person.seniority})` : ""}
          </p>
        </div>
        <CollaboratorBadgesExport
          collaboratorIds={person.collaborators}
          allPeople={allPeople}
          theme={theme}
        />
      </div>
      {assigned && (
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0"
          style={{ backgroundColor: getCLevelColor(cSuiteNodes, assigned, theme) }}
          title={`${assigned.title} — ${assigned.name}`}
        >
          {assigned.title}
        </div>
      )}
    </div>
  );
}

function BoardCardExport({ node, large }) {
  return (
    <div
      className={`bg-white rounded-xl border border-gray-200 flex flex-col items-center justify-center text-center ${
        node.is_vacancy ? "border-dashed bg-gray-50" : ""
      }`}
      style={{ width: large ? "180px" : "160px", height: "120px" }}
    >
      {node.is_vacancy ? (
        <p className="text-xs text-gray-400">Open Position</p>
      ) : (
        <>
          <Avatar name={node.name} photoUrl={node.photo_url} size={large ? "md" : "sm"} />
          <p className="font-bold text-gray-900 mt-1 text-xs">{node.name}</p>
          {node.title && <p className="text-[10px] text-gray-500">{node.title}</p>}
        </>
      )}
    </div>
  );
}

function CSuiteCardExport({ node, index, theme }) {
  const headerColor = getThemeColor(
    node.color,
    theme,
    theme === "selise" || theme === "dark" ? getSeliseCSuiteColor(index) : "#3465a4"
  );
  return (
    <div
      className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col"
      style={{ width: "160px" }}
    >
      <div className="px-3 py-2 flex flex-col items-center text-center">
        {node.is_vacancy ? (
          <p className="text-xs text-gray-400">Open Position</p>
        ) : (
          <>
            <Avatar name={node.name} photoUrl={node.photo_url} size="sm" />
            <p className="font-bold text-gray-900 mt-1 text-xs">{node.name}</p>
            {node.full_title && <p className="text-[10px] text-gray-500">{node.full_title}</p>}
          </>
        )}
      </div>
      <div className="px-3 py-1 text-center" style={{ backgroundColor: headerColor }}>
        <p className="text-white text-sm font-bold">{node.title}</p>
      </div>
    </div>
  );
}

function DivisionCardExport({ division, people, theme, cSuiteNodes, allPeople }) {
  const headerColor = getThemeColor(division.color, theme, getCategoryColor(division.category));
  const grouped = people.reduce((g, p) => {
    const r = p.role_level || "Other";
    (g[r] ||= []).push(p);
    return g;
  }, {});
  const roles = [...ROLE_ORDER, "Other"].filter((r) => grouped[r] && grouped[r].length > 0);
  return (
    <div
      className="bg-white rounded-xl border border-gray-200 flex flex-col"
      style={{ width: "300px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
    >
      <div className="px-3 py-2 rounded-t-xl" style={{ backgroundColor: headerColor }}>
        <div className="flex items-center justify-between">
          <p className="text-white text-xs font-bold leading-tight">{division.department}</p>
          {division.department_code && (
            <span className="text-white text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/20">
              {division.department_code}
            </span>
          )}
        </div>
        <p className="text-white text-[9px] uppercase opacity-80 tracking-wide">
          {division.category}
          {division.unit_size && division.unit_size !== "N/A" && ` · ${division.unit_size}`}
        </p>
      </div>
      <div className="px-3 pt-2 pb-3">
        {roles.map((role) => (
          <div key={role} className="mb-1.5 last:mb-0">
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-0.5">
              {ROLE_LABELS[role] || role}
            </p>
            {grouped[role].map((p) => (
              <PersonRowExport key={p.id} person={p} cSuiteNodes={cSuiteNodes} theme={theme} allPeople={allPeople} />
            ))}
          </div>
        ))}
        {roles.length === 0 && (
          <p className="text-xs text-gray-400 italic text-center py-4">No members</p>
        )}
      </div>
    </div>
  );
}

function Badge({ label, count, color }) {
  return (
    <span
      className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold tracking-wider uppercase border"
      style={{
        backgroundColor: hexToRgba(color, 0.12),
        color,
        borderColor: hexToRgba(color, 0.3),
      }}
    >
      {label}
      {count != null && (
        <span
          className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-bold"
          style={{ backgroundColor: color }}
        >
          {count}
        </span>
      )}
    </span>
  );
}

export default function OrgExportView({ nodes, theme, themeConfig, lastUpdated }) {
  const boardNodes = nodes
    .filter((n) => n.node_type === "board")
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const delegateNode = nodes.find((n) => n.node_type === "delegate");
  const cSuiteNodes = nodes
    .filter((n) => n.node_type === "c_suite")
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const divisionNodes = nodes
    .filter((n) => n.node_type === "division")
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const allPeople = nodes.filter((n) => n.node_type === "person");
  const getPeople = (id) => nodes.filter((n) => n.parent_id === id).sort((a, b) => (a.order || 0) - (b.order || 0));

  const chairman = boardNodes.find((n) => n.order === 0);
  const advisory = boardNodes.filter((n) => n.order !== 0);
  const advisoryRow = [...advisory, ...(delegateNode ? [delegateNode] : [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );

  const isDark = theme === "dark";
  const badgeColor = getBadgeColor(themeConfig, theme) || "#2563eb";
  const boardColor = getBoardColor(themeConfig, theme) || "#2563eb";
  const dividerColor = getDividerColor(themeConfig, theme) || "#caa326";
  const bgColor = getBgColor(themeConfig, theme) || (isDark ? "#1e293b" : "#f8fafc");
  const footerColor = getFooterColor(themeConfig, theme) || undefined;

  return (
    <div style={{ width: "1500px", background: isDark ? "#0f172a" : "#ffffff", padding: "20px 40px 40px" }} className="font-body">
      <div className="flex justify-center mb-6">
        <img
          src="https://media.base44.com/images/public/6a3e1c26ec9e714e5b52016b/70232ae49_SELISE_Logo_1.svg"
          alt="SELISE Group"
          className="h-14 w-auto"
        />
      </div>
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">SELISE Group Official Organogram</h1>
        <p className="text-sm text-gray-500 mt-1.5">A simple view of our people, teams, and leadership across SELISE.</p>
      </div>

      {/* Board */}
      <div className="flex flex-col items-center">
        <div className="mb-4">
          <Badge label="Board of Directors" color={boardColor} />
        </div>
        {chairman && <BoardCardExport node={chairman} large />}
        <div style={{ height: 24 }} />
        {advisoryRow.length > 0 && (
          <div className="flex gap-4 flex-wrap justify-center items-start">
            {advisoryRow.map((n) => (
              <BoardCardExport key={n.id} node={n} />
            ))}
          </div>
        )}
        <div style={{ height: 16 }} />
        {cSuiteNodes.length > 0 && (
          <>
            <div className="mb-4">
              <Badge label="Board of Executives" color={boardColor} />
            </div>
            <div className="flex gap-6 flex-wrap justify-center items-start">
              {cSuiteNodes.map((n, i) => (
                <CSuiteCardExport key={n.id} node={n} index={i} theme={theme} />
              ))}
            </div>
            {/* Vertical connector lines from each C-Suite card down to the divider */}
            <div className="flex gap-6 justify-center">
              {cSuiteNodes.map((n) => (
                <div key={n.id} style={{ width: "160px" }} className="flex justify-center">
                  <div className="w-px h-8" style={{ backgroundColor: hexToRgba(dividerColor, 0.6) }} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Divider — fading chevron arrow sequence matching the live view */}
      <div className="-mt-4 mb-8 flex items-center gap-1">
        <ChevronLeft className="w-4 h-4 -mr-2.5 opacity-30" style={{ color: dividerColor }} />
        <ChevronLeft className="w-5 h-5 -mr-2.5 opacity-50" style={{ color: dividerColor }} />
        <ChevronLeft className="w-6 h-6 -mr-2.5 opacity-70" style={{ color: dividerColor }} />
        <ChevronLeft className="w-7 h-7 -mr-2.5 opacity-85" style={{ color: dividerColor }} />
        <ChevronLeft className="w-8 h-8" style={{ color: dividerColor }} />
        <div className="flex-1 h-px" style={{ background: `linear-gradient(to right, ${hexToRgba(dividerColor, 0.4)}, ${dividerColor}, ${hexToRgba(dividerColor, 0.4)})` }} />
        <ChevronRight className="w-8 h-8" style={{ color: dividerColor }} />
        <ChevronRight className="w-7 h-7 -ml-2.5 opacity-85" style={{ color: dividerColor }} />
        <ChevronRight className="w-6 h-6 -ml-2.5 opacity-70" style={{ color: dividerColor }} />
        <ChevronRight className="w-5 h-5 -ml-2.5 opacity-50" style={{ color: dividerColor }} />
        <ChevronRight className="w-4 h-4 -ml-2.5 opacity-30" style={{ color: dividerColor }} />
      </div>

      {/* Business Units */}
      <div className="rounded-2xl p-6 border border-gray-200" style={{ backgroundColor: bgColor }}>
        {CATEGORIES.map(({ category, label }) => {
          const rowDivisions = divisionNodes.filter((d) => d.category === category);
          if (rowDivisions.length === 0) return null;
          return (
            <div key={category} className="mb-6 last:mb-0">
              <div className="flex items-center justify-center mb-3">
                <Badge label={label} count={rowDivisions.length} color={badgeColor} />
              </div>
              <div className="flex flex-wrap gap-4 justify-center items-stretch">
                {rowDivisions.map((d) => (
                  <DivisionCardExport key={d.id} division={d} people={getPeople(d.id)} theme={theme} cSuiteNodes={cSuiteNodes} allPeople={allPeople} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8">
        <Footer lastUpdated={lastUpdated} footerColor={footerColor} />
      </div>
    </div>
  );
}